import { getCurrentLanguage, getTranslationFn, type TranslationFn } from "@utils/vue-migration/common/gaContext/gaContextTranslations";
import { internalData, loadAsyncData, overrideInternalData, setupInternalData } from "@utils/vue-migration/common/gaContext/gaContextInternals";
import { getFlags } from "@utils/vue-migration/common/gaContext/gaContextFlags";
import { type GaContextAsp, type GaContextCustomer, type GaContextDataSync, type GaContextFlags, Language } from "@newgenerated/shared/schema";
import { assert } from "@utils/assertion";

/*
 * Data that gets loaded for every Vue instance.
 */
export const dataKeysSync = ["currentLanguage", "flags", "customer", "asp"] satisfies (keyof GaContextDataSync)[];

/*
 * Data that can be loaded async if needed.
 */
export type GaContextDataAsync = {
  [Property in Language]: Map<string, string>;
};

export const dataKeysAsync = [...Language.getValues()] satisfies (keyof GaContextDataAsync)[];

export type AsyncLoaders = {
  [Property in keyof GaContextDataAsync]: () => Promise<GaContextDataAsync[Property]>;
};
type AsyncLoadersSimplified = Omit<AsyncLoaders, Language> & { loadTranslations: (lang: Language) => Promise<Map<string, string>> };

/**
 * This needs to run before any Vue instances are created.
 * @param forceOverrideLanguage If set, it forces the context to override the current language. Use with care, added for SSG in Astro
 */
export async function setupGaContext(syncData: GaContextDataSync, asyncLoaders: AsyncLoadersSimplified | AsyncLoaders, forceOverrideLanguage: boolean = false): Promise<void> {
  if ("loadTranslations" in asyncLoaders) {
    const { loadTranslations } = asyncLoaders;
    setupInternalData(syncData, {
      en: () => loadTranslations("en"),
      de: () => loadTranslations("de"),
      es: () => loadTranslations("es"),
      ru: () => loadTranslations("ru"),
      zh: () => loadTranslations("zh"),
      pt: () => loadTranslations("pt"),
      fr: () => loadTranslations("fr"),
    });
  } else {
    setupInternalData(syncData, asyncLoaders);
  }

  if (forceOverrideLanguage) {
    overrideInternalData(syncData.currentLanguage);
  }

  // init TMS texts of current language
  await loadAsyncData(getCurrentLanguage());
}

/*
 * Usage of the context
 */

/**
 * Convention:
 * - every property is a function.
 * - async function names start with "load".
 */

export type GaContext = {
  /**
   * @deprecated Please use the translation function of the context the vue widget generator provides.
   */
  t: TranslationFn;
  currentLanguage: () => Language;
  flags: () => GaContextFlags;
  loadTranslation: (lang: Language) => Promise<TranslationFn>;
  customer: GaContextCustomer | null;
  asp: GaContextAsp | null;
};

export function useGaContext(): GaContext {
  const lang = getCurrentLanguage();

  const t = getTranslationFn(lang);

  const currentLanguage = (): Language => getCurrentLanguage();

  const loadTranslation = async (lang: Language): Promise<TranslationFn> => {
    await loadAsyncData(lang);
    return getTranslationFn(lang);
  };

  assert(internalData.sync.customer.state === "AVAILABLE", "sync data is not initialized");
  assert(internalData.sync.asp.state === "AVAILABLE", "sync data is not initialized");
  return { t, currentLanguage, loadTranslation, flags: getFlags, customer: internalData.sync.customer.value, asp: internalData.sync.asp.value };
}
